import { Controller } from "@hotwired/stimulus"
import { cropMatrix3d } from "../lib/homography"

// Shared PWA image viewer. Fullscreen overlay with pinch/scroll zoom, pan,
// swipe-to-dismiss, and prev/next navigation. Mobile-first design.
//
// Usage: Add data-controller="pwa-image-viewer" to a container. Images within
// that container should have data-action="click->pwa-image-viewer#open" and
// data-pwa-image-viewer-url-param="<url>". A thumbnail whose image carries a
// PERSPECTIVE crop quad adds data-pwa-image-viewer-quad-param="<quad JSON>" — the
// viewer then straightens it (ADR-0082, single CROP toggle, default ON).
//
// Cross-scope handoff (card #4368): a caller outside this controller's DOM
// scope (the question photo strip renders beneath the SurveyJS question, not
// inside the camera-sheet div this controller lives in) can still open this
// exact instance via openUrls(urls, index) -- look it up with
// application.getControllerForElementAndIdentifier(element, "pwa-image-viewer")
// rather than a click action. See field_photo_camera_controller.js, the only
// other controller sharing this element, for the one caller that does this.
//
// Progressive load: an optional data-pwa-image-viewer-preview-param="<url>"
// (the 800x800 Image#preview variant) is painted FIRST, with the loading
// target shown, while the full original is preloaded off-screen. Once the
// original lands it is swapped in and the crop re-solved. On a poor field
// connection the tasker sees a usable photo in a second or two instead of a
// blank viewer for the full 1.5–2.5MB original. Surfaces without a preview
// param behave as before (src = url), just with the loading indicator shown
// until the <img> settles.
//
// Nesting (ADR-0082): the FRAME target carries pan/zoom/swipe (origin centre);
// the IMAGE target carries the crop-straightening matrix3d (origin 0 0). They
// compose without fighting transform-origins.
//
// The modal target must exist somewhere inside the controller scope.
export default class extends Controller {
  static targets = ["modal", "frame", "image", "counter", "prev", "next", "loading", "delete"]

  // State
  scale = 1
  translateX = 0
  translateY = 0
  images = []
  currentIndex = 0
  cropEnabled = true // single CROP toggle, default ON

  // Progressive load bookkeeping. _showToken increments on every show/close so a
  // full-original preload that lands after prev/next/close is ignored rather
  // than clobbering whatever is on screen now. _awaitingFull is true while the
  // <img> is showing a preview and the original is still streaming in.
  _showToken = 0
  _awaitingFull = false

  // Touch tracking
  _startTouches = null
  _startScale = 1
  _startTranslateX = 0
  _startTranslateY = 0
  _isPanning = false
  _isDismissing = false
  _dismissStartY = 0
  _lastTap = 0

  connect() {
    this._boundKeydown = this._onKeydown.bind(this)
  }

  disconnect() {
    document.removeEventListener("keydown", this._boundKeydown)
  }

  // -- Public actions --

  open(event) {
    event.preventDefault()
    event.stopPropagation()

    const url = event.params.url
    if (!url) return

    // Collect all image URLs within this controller's scope, plus any perspective
    // crop quad (data-pwa-image-viewer-quad-param) and progressive-load preview
    // (data-pwa-image-viewer-preview-param) carried alongside. De-dup on URL.
    this.images = []
    this.element.querySelectorAll("[data-pwa-image-viewer-url-param]").forEach(el => {
      const u = el.dataset.pwaImageViewerUrlParam
      if (u && !this.images.some(img => img.url === u)) {
        this.images.push({
          url:     u,
          quad:    el.dataset.pwaImageViewerQuadParam || null,
          preview: el.dataset.pwaImageViewerPreviewParam || null
        })
      }
    })

    this._onDelete = null
    this._present(Math.max(0, this.images.findIndex(img => img.url === url)))
  }

  // Public entry point for callers outside this controller's DOM scope (card
  // #4368). `urls` is the complete set to swipe between (e.g. one question's
  // photos, in order); `index` is which one was tapped. No crop quad or
  // progressive preview param here -- those only exist for server-side Image
  // records, and this entry point also serves local blob: URLs that have
  // neither.
  //
  // `onDelete` (card #4744) is an opt-in hook: callers that know how to
  // remove a photo from its owning question (the thumbnail strip and the
  // background-upload tile strip, both via lib/photo_removal.js) pass it in
  // and the viewer shows a delete button; callers that don't (chat, the
  // Images tab, My Photos) get the viewer exactly as before, no delete
  // button, since there's nothing safe to call.
  openUrls(urls, index, { onDelete = null } = {}) {
    if (!Array.isArray(urls) || !urls.length) return

    this.images = urls.map(url => ({ url, quad: null, preview: null }))
    this._onDelete = onDelete
    this._present(Math.max(0, Math.min(index || 0, this.images.length - 1)))
  }

  // Shared by open() and openUrls() -- show the current image and reveal
  // the modal.
  _present(startIndex) {
    this.currentIndex = startIndex
    this._showCurrent()
    this._resetTransform()
    this.modalTarget.classList.remove("hidden")
    if (this.hasDeleteTarget) this.deleteTarget.classList.toggle("hidden", !this._onDelete)
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", this._boundKeydown)
  }

  // Delete button action (card #4744) -- only reachable when openUrls() was
  // given an onDelete hook, see openUrls' doc comment. One-tap confirm (a
  // native confirm(), no custom modal needed for a single destructive
  // action) before calling it. After deletion, shows the next photo in the
  // same position, or the previous one if the deleted photo was last, or
  // closes the viewer entirely if that was the only photo left.
  deleteCurrent() {
    if (!this._onDelete) return
    const current = this.images[this.currentIndex]
    if (!current) return
    if (!window.confirm("Delete this photo?")) return

    this._onDelete(current.url)
    this.images.splice(this.currentIndex, 1)

    if (!this.images.length) {
      this.close()
      return
    }
    if (this.currentIndex >= this.images.length) this.currentIndex = this.images.length - 1
    this._resetTransform()
    this._showCurrent()
  }

  close() {
    // Invalidate any in-flight full-original preload so it can't repaint the
    // image after the viewer is dismissed and reopened on something else.
    this._showToken++
    this._awaitingFull = false
    this._setLoading(false)
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    document.removeEventListener("keydown", this._boundKeydown)
    this._resetTransform()
  }

  prev() {
    if (this.images.length <= 1) return
    this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.images.length - 1
    this._resetTransform()
    this._showCurrent()
  }

  next() {
    if (this.images.length <= 1) return
    this.currentIndex = this.currentIndex < this.images.length - 1 ? this.currentIndex + 1 : 0
    this._resetTransform()
    this._showCurrent()
  }

  backdropClose(event) {
    // Only close if tapping the backdrop itself, not the image
    if (event.target === event.currentTarget && this.scale <= 1) {
      this.close()
    }
  }

  // -- Mouse wheel zoom --

  wheel(event) {
    event.preventDefault()
    const delta = event.deltaY > 0 ? -0.15 : 0.15
    this._zoom(this.scale + delta, event.clientX, event.clientY)
  }

  // -- Touch events --

  touchstart(event) {
    const touches = event.touches

    // Double-tap detection
    const now = Date.now()
    if (touches.length === 1 && now - this._lastTap < 300) {
      event.preventDefault()
      this._doubleTap(touches[0].clientX, touches[0].clientY)
      this._lastTap = 0
      return
    }
    this._lastTap = touches.length === 1 ? now : 0

    this._startTouches = this._captureTouches(touches)
    this._startScale = this.scale
    this._startTranslateX = this.translateX
    this._startTranslateY = this.translateY
    this._isPanning = touches.length === 1 && this.scale > 1
    this._isDismissing = touches.length === 1 && this.scale <= 1
    this._dismissStartY = touches.length === 1 ? touches[0].clientY : 0
  }

  touchmove(event) {
    const touches = event.touches
    if (!this._startTouches) return

    if (touches.length === 2 && this._startTouches.length === 2) {
      // Pinch zoom -- compute from gesture-start state to prevent translation drift.
      event.preventDefault()
      const startDist = this._distance(this._startTouches[0], this._startTouches[1])
      const currentDist = this._distance(touches[0], touches[1])
      const midX = (touches[0].clientX + touches[1].clientX) / 2
      const midY = (touches[0].clientY + touches[1].clientY) / 2
      const newScale = Math.min(Math.max(this._startScale * (currentDist / startDist), 1), 5)
      const ratio = newScale / this._startScale
      const cx = window.innerWidth / 2
      const cy = window.innerHeight / 2
      this.scale = newScale
      this.translateX = (midX - cx) * (1 - ratio) + this._startTranslateX * ratio
      this.translateY = (midY - cy) * (1 - ratio) + this._startTranslateY * ratio
      this._applyTransform()
    } else if (touches.length === 1) {
      const dx = touches[0].clientX - this._startTouches[0].clientX
      const dy = touches[0].clientY - this._startTouches[0].clientY

      if (this._isPanning) {
        // Pan when zoomed in
        event.preventDefault()
        this.translateX = this._startTranslateX + dx
        this.translateY = this._startTranslateY + dy
        this._applyTransform()
      } else if (this._isDismissing) {
        // Swipe down to dismiss -- translate the image and fade the backdrop
        const absDy = Math.abs(dy)
        if (absDy > 10) {
          event.preventDefault()
          // Swipe transform rides the FRAME so it never clobbers the inner crop
          // matrix3d (ADR-0082 nesting).
          const el = this._panZoomElement()
          if (el) el.style.transform = `translateY(${dy}px) scale(${1 - absDy * 0.001})`
          this.modalTarget.style.opacity = Math.max(0.3, 1 - absDy / 400)
        }
      }
    }
  }

  touchend(event) {
    const touches = event.changedTouches

    if (this._isDismissing && touches.length >= 1) {
      const dy = touches[0].clientY - this._dismissStartY
      if (Math.abs(dy) > 120) {
        // Dismiss
        this.modalTarget.style.opacity = ""
        this.close()
        return
      }
      // Snap back — clear the swipe transform on the frame, then re-apply pan/zoom.
      const el = this._panZoomElement()
      if (el) el.style.transform = ""
      this.modalTarget.style.opacity = ""
      this._applyTransform()
    }

    // Clamp scale
    if (this.scale < 1) {
      this._resetTransform()
    }

    this._startTouches = null
    this._isPanning = false
    this._isDismissing = false
  }

  // -- Mouse drag (desktop pan when zoomed) --

  mousedown(event) {
    if (this.scale <= 1) return
    event.preventDefault()
    this._mouseStart = { x: event.clientX, y: event.clientY }
    this._startTranslateX = this.translateX
    this._startTranslateY = this.translateY
    this._mouseMoveHandler = this._onMouseMove.bind(this)
    this._mouseUpHandler = this._onMouseUp.bind(this)
    window.addEventListener("mousemove", this._mouseMoveHandler)
    window.addEventListener("mouseup", this._mouseUpHandler)
  }

  // -- Private --

  _onMouseMove(event) {
    const dx = event.clientX - this._mouseStart.x
    const dy = event.clientY - this._mouseStart.y
    this.translateX = this._startTranslateX + dx
    this.translateY = this._startTranslateY + dy
    this._applyTransform()
  }

  _onMouseUp() {
    window.removeEventListener("mousemove", this._mouseMoveHandler)
    window.removeEventListener("mouseup", this._mouseUpHandler)
  }

  _doubleTap(x, y) {
    if (this.scale > 1) {
      this._resetTransform()
    } else {
      this._zoom(2.5, x, y)
    }
  }

  _zoom(newScale, originX, originY) {
    const clampedScale = Math.min(Math.max(newScale, 1), 5)
    if (clampedScale === 1) {
      this._resetTransform()
      return
    }

    const ratio = clampedScale / this.scale
    const cx = window.innerWidth / 2
    const cy = window.innerHeight / 2
    this.translateX = (originX - cx) * (1 - ratio) + this.translateX * ratio
    this.translateY = (originY - cy) * (1 - ratio) + this.translateY * ratio
    this.scale = clampedScale
    this._applyTransform()
  }

  _resetTransform() {
    this.scale = 1
    this.translateX = 0
    this.translateY = 0
    this._applyTransform()
    if (this.hasFrameTarget) {
      this.frameTarget.style.transform = ""
      this.frameTarget.style.opacity = ""
    }
  }

  // Pan/zoom lives on the FRAME (origin centre), so it composes cleanly with the
  // crop matrix3d on the inner <img> (origin 0 0) — ADR-0082 nesting.
  _applyTransform() {
    const el = this._panZoomElement()
    if (!el) return
    if (this.scale > 1) {
      const imgW = el.offsetWidth * this.scale
      const imgH = el.offsetHeight * this.scale
      const maxTx = Math.max(0, (imgW - window.innerWidth) / 2)
      const maxTy = Math.max(0, (imgH - window.innerHeight) / 2)
      this.translateX = Math.max(-maxTx, Math.min(maxTx, this.translateX))
      this.translateY = Math.max(-maxTy, Math.min(maxTy, this.translateY))
    }
    el.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`
  }

  // The element carrying pan/zoom/swipe — the frame when nested, else the image
  // (defensive: older markup without the frame wrapper still pans the image).
  _panZoomElement() {
    if (this.hasFrameTarget) return this.frameTarget
    if (this.hasImageTarget) return this.imageTarget
    return null
  }

  // Apply (or clear) the crop-straightening matrix3d on the inner <img>. A
  // perspective crop quad ([[x,y]×4] JSON) is straightened + framed via a single
  // homography (transform-origin 0 0). Pixel-dependent, so re-solved once the
  // image's natural size is known. CROP toggle default ON. No quad → plain image.
  _applyCrop(quadJson) {
    if (!this.hasImageTarget) return
    const img = this.imageTarget
    img.style.transform = ""
    img.style.transformOrigin = ""
    this._pendingCropQuad = null

    if (!this.cropEnabled || !quadJson) return

    const solve = () => this._solveCrop(quadJson)
    if (img.complete && img.naturalWidth) {
      solve()
    } else {
      this._pendingCropQuad = quadJson
      if (!this._boundCropOnLoad) {
        this._boundCropOnLoad = () => {
          if (this._pendingCropQuad) this._solveCrop(this._pendingCropQuad)
        }
        img.addEventListener("load", this._boundCropOnLoad)
      }
    }
  }

  _solveCrop(quadJson) {
    if (!this.hasImageTarget || !this.hasFrameTarget) return
    const img = this.imageTarget

    let quad
    try {
      quad = JSON.parse(quadJson)
    } catch (e) {
      return
    }

    const matrix = cropMatrix3d(this.frameTarget, img, quad)
    if (!matrix) return
    img.style.transformOrigin = "0 0"
    img.style.transform = matrix
  }

  _showCurrent() {
    if (!this.hasImageTarget || this.images.length === 0) return
    const current = this.images[this.currentIndex]
    const token = ++this._showToken
    const img = this.imageTarget
    this._ensureImageSettleListeners()

    // Preview first, full original behind it. A preview identical to the url
    // (legacy rows where Image#preview fell back to url) is no preview at all.
    const preview = current.preview && current.preview !== current.url ? current.preview : null

    this._setLoading(true)
    if (preview) {
      this._awaitingFull = true
      img.src = preview
      this._applyCrop(current.quad)
      this._preloadFull(current, token)
    } else {
      this._awaitingFull = false
      img.src = current.url
      this._applyCrop(current.quad)
    }

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = this.images.length > 1
        ? `${this.currentIndex + 1} / ${this.images.length}`
        : ""
    }

    if (this.hasPrevTarget && this.hasNextTarget) {
      const hide = this.images.length <= 1
      this.prevTarget.classList.toggle("hidden", hide)
      this.nextTarget.classList.toggle("hidden", hide)
    }
  }

  // Fetch the full original off-screen. When it lands -- and the viewer is
  // still on the same show (token) -- swap it into the <img> and re-solve the
  // crop, since the homography is solved off natural dimensions and the
  // preview's differ from the original's. On error leave the preview showing:
  // never a broken-image glyph.
  _preloadFull(current, token) {
    const loader = new Image()
    loader.onload = () => {
      if (token !== this._showToken) return
      this._awaitingFull = false
      this.imageTarget.src = current.url
      this._applyCrop(current.quad)
      this._setLoading(false)
    }
    loader.onerror = () => {
      if (token !== this._showToken) return
      this._awaitingFull = false
      this._setLoading(false)
    }
    loader.src = current.url
  }

  // Hide the loading indicator once the <img> itself settles -- unless we are
  // still waiting on the full original behind a preview, in which case the
  // preload's own handlers own the hide. Bound once, lazily, matching the
  // _boundCropOnLoad pattern above.
  _ensureImageSettleListeners() {
    if (this._boundImageSettled || !this.hasImageTarget) return
    this._boundImageSettled = () => {
      if (!this._awaitingFull) this._setLoading(false)
    }
    this.imageTarget.addEventListener("load", this._boundImageSettled)
    this.imageTarget.addEventListener("error", this._boundImageSettled)
  }

  _setLoading(visible) {
    if (!this.hasLoadingTarget) return
    this.loadingTarget.classList.toggle("hidden", !visible)
  }

  _onKeydown(event) {
    switch (event.key) {
      case "Escape":
        this.close()
        break
      case "ArrowLeft":
        event.preventDefault()
        this.prev()
        break
      case "ArrowRight":
        event.preventDefault()
        this.next()
        break
    }
  }

  _captureTouches(touches) {
    return Array.from(touches).map(t => ({ clientX: t.clientX, clientY: t.clientY }))
  }

  _distance(a, b) {
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
  }
}
